/**
 * @architecture-header 1
 * @layer host-config
 * @domain browser
 * @responsibility Load non-secret environment endpoints at runtime so one Web artifact can be promoted.
 * @state-owner immutable browser runtime configuration
 * @allowed-imports browser fetch and Vite development environment
 * @forbidden service-role keys; database URLs; silent production demo fallback
 * @public-api BrowserRuntimeConfig and loadBrowserRuntimeConfig
 * @side-effects fetches /runtime-config.json outside local development
 * @tests frontend typecheck and deployment smoke test
 */

export interface BrowserRuntimeConfig {
  apiUrl: string;
  demoMode: boolean;
  localRunId: string;
  supabasePublishableKey: string;
  supabaseUrl: string;
}

function parseRuntimeConfig(value: unknown): BrowserRuntimeConfig {
  if (!value || typeof value !== 'object') throw new Error('Runtime configuration is invalid');
  const record = value as Record<string, unknown>;
  const apiUrl = typeof record.apiUrl === 'string' ? record.apiUrl.trim() : '';
  const supabaseUrl = typeof record.supabaseUrl === 'string' ? record.supabaseUrl.trim() : '';
  const supabasePublishableKey =
    typeof record.supabasePublishableKey === 'string' ? record.supabasePublishableKey.trim() : '';
  const demoMode = record.demoMode === true;
  const localRunId = typeof record.localRunId === 'string' ? record.localRunId.trim() : '';
  if (!demoMode && (!apiUrl || !supabaseUrl || !supabasePublishableKey)) {
    throw new Error('Runtime configuration is incomplete');
  }
  for (const endpoint of [apiUrl, supabaseUrl]) {
    if (!endpoint) continue;
    const parsed = new URL(endpoint);
    if (
      parsed.protocol !== 'https:' &&
      !(parsed.protocol === 'http:' && ['127.0.0.1', 'localhost'].includes(parsed.hostname))
    ) {
      throw new Error('Runtime endpoint must use HTTPS or local HTTP');
    }
  }
  return { apiUrl, demoMode, localRunId, supabasePublishableKey, supabaseUrl };
}

export async function loadBrowserRuntimeConfig(): Promise<BrowserRuntimeConfig> {
  if (import.meta.env.DEV) {
    return parseRuntimeConfig({
      apiUrl: import.meta.env.VITE_VISPO_API_URL?.trim() ?? '',
      demoMode: import.meta.env.VITE_VISPO_DEMO === 'true',
      localRunId: import.meta.env.VITE_VISPO_LOCAL_RUN_ID?.trim() ?? '',
      supabasePublishableKey:
        import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim() ??
        import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() ??
        '',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL?.trim() ?? '',
    });
  }
  const response = await fetch('/runtime-config.json', {
    cache: 'no-store',
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('Runtime configuration could not be loaded');
  return parseRuntimeConfig(await response.json());
}
